
import { ChatTurn } from "../shared/timeline.js";
import { AppMessageScroller } from "./app-message-scroller.js";
import { AppMessage } from "./app-message.js";
import { AppWorkGroup } from "./app-work-group.js";

const styles = css`
  :host { display: block; height: 100%; min-height: 0; width: 100%; }
  app-message-scroller { height: 100%; }
  .transcript { box-sizing: border-box; margin: 0 auto; max-width: 768px; min-height: 100%; padding: 12px 16px var(--composer-clearance, 160px); width: 100%; }
  .turn { min-width: 0; }
  .turn + .turn { margin-top: 4px; }
  .user-row { padding-bottom: 16px; }
  .work-row { padding-bottom: 16px; }
  .answer-row { padding-bottom: 16px; }
  .empty { align-content: center; display: grid; flex: 1; justify-items: center; min-height: 100%; padding-bottom: 96px; text-align: center; }
  .empty h2 { color: var(--foreground); font-size: clamp(28px, 4vw, 40px); font-weight: 700; letter-spacing: -0.03em; line-height: 1.08; margin: 0 0 12px; }
  .empty p { color: var(--muted-foreground); font-size: 0.9375rem; line-height: 1.5; margin: 0; max-width: 420px; }
  @media (max-width: 767px) { .transcript { padding: 10px 12px var(--composer-clearance, 152px); } }
`;

export class AppChatView extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppChatView)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["turns","data-turns","emptytitle","data-empty-title","emptydescription","data-empty-description"];

  #turns = [];
  #emptyTitle = "What are we working on?";
  #emptyDescription = "Start a thread and S.T.R.I.D.E. will keep the context here.";
  #turnsSet = false;
  #emptyTitleSet = false;
  #emptyDescriptionSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get turns() {
    return this.#turns;
  }

  set turns(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#turns, next)) return;
    this.#turns = next;
    this.#turnsSet = true;
    this.#updateBindings('turns');
  }

  get emptyTitle() {
    return this.#emptyTitle;
  }

  set emptyTitle(value) {
    const raw = value ?? "What are we working on?";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#emptyTitle, next)) return;
    this.#emptyTitle = next;
    this.#emptyTitleSet = true;
    this.#updateBindings('emptyTitle');
  }

  get emptyDescription() {
    return this.#emptyDescription;
  }

  set emptyDescription(value) {
    const raw = value ?? "Start a thread and S.T.R.I.D.E. will keep the context here.";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#emptyDescription, next)) return;
    this.#emptyDescription = next;
    this.#emptyDescriptionSet = true;
    this.#updateBindings('emptyDescription');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-turns')) this.turns = this.dataset.turns;
    if (this.hasAttribute('data-empty-title')) this.emptyTitle = this.dataset.emptyTitle;
    if (this.hasAttribute('data-empty-description')) this.emptyDescription = this.dataset.emptyDescription;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.turns = props.turns;
      this.emptyTitle = props.emptyTitle;
      this.emptyDescription = props.emptyDescription;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#turnsSet) this.turns = this.dataset.turns;
    if (!this.#emptyTitleSet) this.emptyTitle = this.dataset.emptyTitle;
    if (!this.#emptyDescriptionSet) this.emptyDescription = this.dataset.emptyDescription;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'turns':
      case 'data-turns':
        this.turns = newValue;
        break;
      case 'emptytitle':
      case 'data-empty-title':
        this.emptyTitle = newValue;
        break;
      case 'emptydescription':
      case 'data-empty-description':
        this.emptyDescription = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><app-message-scroller><!--argon-text:0-->${""}<!--/argon-text:0--><div class="transcript" data-messages><!--argon-text:1-->${(this.#turns.length === 0) ? `<div class="empty" data-empty><h2>${this.#escText(this.#emptyTitle)}</h2><p>${this.#escText(this.#emptyDescription)}</p></div>` : this.#turns.map((turn) => `<section class="turn">${turn.hasUser ? `<div class="user-row"><app-message data-message-id="${this.#attrEsc(turn.user.id)}" data-seq="${this.#attrEsc(turn.user.seq)}" data-role="${this.#attrEsc(turn.user.role)}" data-kind="${this.#attrEsc(turn.user.kind)}" data-format="${this.#attrEsc(turn.user.format)}" data-text="${this.#attrEsc(turn.user.text)}" data-pending="${this.#attrEsc(turn.user.pending)}">${""}</app-message></div>` : ""}${turn.hasWork ? `<div class="work-row"><app-work-group data-label="${this.#attrEsc(turn.workLabel)}" data-segments="${this.#attrEsc(turn.segments)}" data-running="${this.#attrEsc(turn.running)}" data-started-at="${this.#attrEsc(turn.startedAt)}">${""}</app-work-group></div>` : ""}${turn.hasAnswer ? `<div class="answer-row"><app-message data-message-id="${this.#attrEsc(turn.answer.id)}" data-seq="${this.#attrEsc(turn.answer.seq)}" data-role="${this.#attrEsc(turn.answer.role)}" data-kind="${this.#attrEsc(turn.answer.kind)}" data-format="${this.#attrEsc(turn.answer.format)}" data-text="${this.#attrEsc(turn.answer.text)}" data-pending="${this.#attrEsc(turn.answer.pending)}">${""}</app-message></div>` : ""}</section>`).join("")}<!--/argon-text:1--></div></app-message-scroller>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'turns' || prop === 'emptyTitle' || prop === 'emptyDescription') {
      const __argonNext = `${(this.#turns.length === 0) ? `<div class="empty" data-empty><h2>${this.#escText(this.#emptyTitle)}</h2><p>${this.#escText(this.#emptyDescription)}</p></div>` : this.#turns.map((turn) => `<section class="turn">${turn.hasUser ? `<div class="user-row"><app-message data-message-id="${this.#attrEsc(turn.user.id)}" data-seq="${this.#attrEsc(turn.user.seq)}" data-role="${this.#attrEsc(turn.user.role)}" data-kind="${this.#attrEsc(turn.user.kind)}" data-format="${this.#attrEsc(turn.user.format)}" data-text="${this.#attrEsc(turn.user.text)}" data-pending="${this.#attrEsc(turn.user.pending)}">${""}</app-message></div>` : ""}${turn.hasWork ? `<div class="work-row"><app-work-group data-label="${this.#attrEsc(turn.workLabel)}" data-segments="${this.#attrEsc(turn.segments)}" data-running="${this.#attrEsc(turn.running)}" data-started-at="${this.#attrEsc(turn.startedAt)}">${""}</app-work-group></div>` : ""}${turn.hasAnswer ? `<div class="answer-row"><app-message data-message-id="${this.#attrEsc(turn.answer.id)}" data-seq="${this.#attrEsc(turn.answer.seq)}" data-role="${this.#attrEsc(turn.answer.role)}" data-kind="${this.#attrEsc(turn.answer.kind)}" data-format="${this.#attrEsc(turn.answer.format)}" data-text="${this.#attrEsc(turn.answer.text)}" data-pending="${this.#attrEsc(turn.answer.pending)}">${""}</app-message></div>` : ""}</section>`).join("")}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
